import { minorUnitExponent, minorUnitsPerUnit } from "@dentacore/shared-types";

/**
 * Money, from integer minor units.
 *
 * **One implementation, used by the file header and the Billing tab.** They
 * render the same figures and a patient reads both on the same screen, so two
 * formatters would eventually disagree about the same number — which on a bill
 * is a support call rather than a cosmetic bug.
 *
 * **The exponent comes from the currency, never from an assumption of two
 * decimal places.** `clinics.currency` may be a zero-decimal currency, and
 * `Intl` is what knows which. Falling back to raw minor units on an unknown
 * code is deliberate: a number a human can question beats a number that is
 * quietly wrong by a factor of a hundred.
 */
export function formatMoney(cents: number, currency: string): string {
  try {
    // The exponent comes from the shared helper, not from a second reading of
    // `resolvedOptions()` here — one rule, one place (QA #69). The formatter is
    // still built locally because it is what renders the symbol and grouping in
    // the viewer's own locale.
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
      cents / 10 ** minorUnitExponent(currency),
    );
  } catch {
    return `${String(cents)} (${currency})`;
  }
}

/**
 * How many minor units one unit of this currency has. `USD` → 100, `JPY` → 1.
 *
 * **Re-exported from `@dentacore/shared-types` rather than implemented here**
 * (QA #69). The API converts the same money when it imports a price-list CSV,
 * and until this round it did so by multiplying by a hardcoded hundred — two
 * implementations of one rule, disagreeing by a factor of a hundred in any
 * zero-decimal currency. One definition now serves both sides.
 */
export { minorUnitsPerUnit };

/**
 * What someone typed into a price box, as minor units — or null if it was not a
 * positive number.
 *
 * **Multiplies by the currency's own exponent**, not by 100. Hardcoding a
 * hundred here would bill a patient a hundred times over in a zero-decimal
 * currency, which is the kind of bug that is only ever found by the patient.
 */
export function parseMoney(typed: string, currency: string): number | null {
  const value = Number(typed.trim());
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * minorUnitsPerUnit(currency));
}

/**
 * Amounts in several currencies, **each shown in its own and never added**
 * (QA4-D6) — `$20.00 · LBP 1,790,000`. Nothing at all is a zero in `fallback`,
 * the home currency, so a file with nothing billed still reads as a figure.
 */
export function formatAmounts(
  amounts: readonly { currency: string; cents: number }[],
  fallback: string,
): string {
  if (amounts.length === 0) return formatMoney(0, fallback);
  return amounts.map((a) => formatMoney(a.cents, a.currency)).join(" · ");
}
